import { Button } from "@dmca/frontend/components/button";
import { Input } from "@dmca/frontend/components/input";
import { GlobalNavbarCenter, PageHeader } from "@dmca/frontend/components/nav";
import { checkPermissionsInLoader } from "@dmca/frontend/hooks";
import { Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { toast } from "react-toastify";
import { useDrawerLayoutContext } from "../../-DrawerLayoutContext";
import { useEffect, useState } from "react";

export const Route = createFileRoute(
  "/_authed/_drawerLayout/admin/notification"
)({
  component: RouteComponent,
  loader: async ({ context }) => {
    await checkPermissionsInLoader("Admin::UpdateNotification", context);
    const { trpcQcProxy: trpc, qc } = context;
    const notificationWindow = await qc.ensureQueryData(
      trpc.notification.getWindow.queryOptions()
    );
    return { notificationWindow };
  },
  head: () => ({
    meta: [
      {
        title: "App Notifications | DNA Dashboard",
      },
    ],
  }),
});

const listItems = [
  {
    label: "Email Reports",
    value: "email",
  },
  {
    label: "IOT Device Heartbeat",
    value: "iot-heartbeat",
  },
  {
    label: "Call Help",
    value: "call-help",
  },
  {
    label: "Job Notification",
    value: "job-notification",
  },
  {
    label: "Kanban Order Approval",
    value: "kanban-order-approval",
  },
];

function RouteComponent() {
  const { notificationWindow } = Route.useLoaderData();
  const { trpcQcProxy: trpc } = Route.useRouteContext();
  const qc = useQueryClient();
  const navigate = Route.useNavigate();
  const [start, setStart] = useState(notificationWindow?.start ?? "");
  const [end, setEnd] = useState(notificationWindow?.end ?? "");
  const timeZone =
    notificationWindow?.timeZone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const updateWindow = useMutation(
    trpc.notification.setWindow.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries(trpc.notification.getWindow.queryFilter());
        toast.success("Notification hours updated.");
      },
      onError: () => toast.error("Failed to update notification hours."),
    })
  );
  const id = useMatches({
    select: (matches) => {
      const match = matches[matches.length - 1];
      return match.id.split("/").pop() || "";
    },
  });
  const {
    selectedItem: [, setSelectedItem],
  } = useDrawerLayoutContext({
    showSearchBar: false,
    listItems,
    onItemSelected: (item: { label: string; value: string }) => {
      navigate({
        to: `/admin/notification/${item.value}`,
      });
    },
  });

  useEffect(() => {
    if (id) {
      const selectedItem = listItems.find((item) => item.value === id);
      if (selectedItem) {
        setSelectedItem(selectedItem);
      }
    }
  }, [id, setSelectedItem]);

  return (
    <>
      <GlobalNavbarCenter>
        <Stack
          component="form"
          direction="row"
          spacing={1}
          alignItems="center"
          onSubmit={(event) => {
            event.preventDefault();
            updateWindow.mutate({ start, end, timeZone });
          }}
        >
          <Typography variant="body2">Notify from</Typography>
          <Input
            aria-label="Notification start time"
            type="time"
            required
            size="small"
            fullWidth={false}
            value={start}
            onChange={(event) => setStart(event.target.value)}
            slotProps={{ htmlInput: { step: 300 } }}
            sx={{ width: 130 }}
          />
          <Typography variant="body2">to</Typography>
          <Input
            aria-label="Notification end time"
            type="time"
            required
            size="small"
            fullWidth={false}
            value={end}
            onChange={(event) => setEnd(event.target.value)}
            slotProps={{ htmlInput: { step: 60 } }}
            sx={{ width: 130 }}
          />
          <Button type="submit" size="small" disabled={updateWindow.isPending}>
            Save
          </Button>
        </Stack>
      </GlobalNavbarCenter>
      <PageHeader>Manage App Notifications</PageHeader>
      <Outlet />
    </>
  );
}
